import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { messagesApi } from "@/api";
import type { MessageStatus } from "@/types";
import HelpTip from "@/components/ui/HelpTip";

const statusColors: Record<MessageStatus, string> = {
  QUEUED: "bg-yellow-100 text-yellow-800",
  SENDING: "bg-blue-100 text-blue-800",
  DELIVERED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  RECEIVED: "bg-purple-100 text-purple-800",
  CANCELLED: "bg-slate-100 text-slate-500",
};

export default function Messages() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const cancelMutation = useMutation({
    mutationFn: (id: string) => messagesApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });

  const params: Record<string, string> = {
    page: String(page),
    limit: "30",
  };
  if (search) params.q = search;
  if (statusFilter) params.status = statusFilter;

  const { data, isLoading } = useQuery({
    queryKey: ["messages", params],
    queryFn: () => messagesApi.list(params),
  });

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">メッセージ</h2>

      <div className="flex gap-3">
        <input
          type="text"
          placeholder="電話番号 / 本文で検索..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-md text-sm w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-md text-sm"
        >
          <option value="">全ステータス</option>
          <option value="QUEUED">待機中</option>
          <option value="SENDING">送信中</option>
          <option value="DELIVERED">配信済</option>
          <option value="FAILED">失敗</option>
          <option value="RECEIVED">受信</option>
          <option value="CANCELLED">キャンセル</option>
        </select>
      </div>

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">日時</th>
                  <th className="px-4 py-3 text-left font-medium">方向</th>
                  <th className="px-4 py-3 text-left font-medium">宛先</th>
                  <th className="px-4 py-3 text-left font-medium">本文</th>
                  <th className="px-4 py-3 text-left font-medium">ステータス<HelpTip text="待機中→送信中→配信済 or 失敗。受信は顧客からの返信" /></th>
                  <th className="px-4 py-3 text-left font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data?.data.map((msg) => (
                  <tr key={msg.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      {new Date(msg.createdAt).toLocaleString("ja-JP")}
                    </td>
                    <td className="px-4 py-3">
                      {msg.direction === "OUTBOUND" ? "送信" : "受信"}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {msg.direction === "OUTBOUND" ? msg.to : msg.from}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate">
                      {msg.body}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[msg.status]}`}
                      >
                        {msg.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {msg.status === "QUEUED" && (
                        <button
                          onClick={() => {
                            if (confirm("この送信をキャンセルしますか？")) cancelMutation.mutate(msg.id);
                          }}
                          className="text-red-600 hover:underline text-xs"
                        >
                          キャンセル
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                前へ
              </button>
              <span className="px-3 py-1 text-sm">
                {page} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                次へ
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
